/**
 * Tuile du **grimoire** (`GET /me/collection`, arbitrage A-27 / F13).
 *
 * La règle de masquage est portée par le **type**, pas par une condition d'affichage : une carte
 * non possédée (`possedee: false`) n'a ni `description`, ni `effets`, ni `tags` dans ses
 * propriétés, donc rien de tel ne peut se retrouver dans le DOM — même par erreur d'un futur
 * appelant. Une carte possédée, elle, porte ces trois champs (facultatifs : une carte sans
 * description publiée n'en invente pas).
 *
 * Le nom reste toujours affiché : c'est la seule information que le serveur publie pour une carte
 * verrouillée (avec sa rareté et ses points, nécessaires aux filtres et aux compteurs).
 */
import { useTranslation } from 'react-i18next';

import type { Rarete } from '@elemora/shared/api';

/** Champs communs aux deux états d'une carte du grimoire. */
interface CarteGrimoireCommune {
  readonly cardId: string;
  /** Nom déjà traduit (clé de contenu résolue par l'écran). */
  readonly nom: string;
  readonly rarete: Rarete;
  /** Libellé de rareté déjà traduit. */
  readonly rareteLibelle: string;
  /** Élément publié du manifeste, `null` si la carte n'y figure pas. */
  readonly element: string | null;
  /** Libellé d'élément déjà traduit, `null` si la carte n'est pas au manifeste. */
  readonly elementLibelle: string | null;
  readonly points: number;
}

/** Carte du grimoire, dans l'un des deux états exclusifs. */
export type CarteGrimoireVue =
  | (CarteGrimoireCommune & {
      readonly possedee: true;
      /** Description publiée, `null` si le contenu n'en fournit pas. */
      readonly description: string | null;
      /** Effets déjà mis en mots par l'écran (`resumeEffet`). */
      readonly effets: readonly string[];
      readonly tags: readonly string[];
      /** Date d'obtention déjà formatée (`Intl`), `null` si le serveur n'en fournit pas. */
      readonly obtenueLe: string | null;
    })
  | (CarteGrimoireCommune & { readonly possedee: false });

export interface ProprietesCarteGrimoire {
  readonly carte: CarteGrimoireVue;
}

export function CarteGrimoire({ carte }: ProprietesCarteGrimoire) {
  const { t } = useTranslation();
  const points = t('grimoire.points', { count: carte.points });

  return (
    <li
      className={`carte-grimoire${carte.possedee ? ' carte-grimoire--possedee' : ' carte-grimoire--verrouillee'}`}
      data-card={carte.cardId}
      data-possedee={carte.possedee ? 'oui' : 'non'}
      data-rarete={carte.rarete}
      data-element={carte.element ?? undefined}
      title={carte.nom}
    >
      <h3 className="carte-grimoire__nom">{carte.nom}</h3>
      <p className="carte-grimoire__meta">
        <span className="carte-grimoire__rarete">{carte.rareteLibelle}</span>
        {carte.elementLibelle === null ? null : (
          <span className="carte-grimoire__element">{carte.elementLibelle}</span>
        )}
        <span className="carte-grimoire__points">{points}</span>
      </p>

      {carte.possedee ? (
        <div className="carte-grimoire__details">
          <p className="carte-grimoire__etat carte-grimoire__etat--possedee" data-etat="possedee">
            {t('grimoire.possedee')}
            {carte.obtenueLe === null ? null : (
              <span className="carte-grimoire__date">
                {' '}
                {t('grimoire.obtenueLe', { date: carte.obtenueLe })}
              </span>
            )}
          </p>
          {carte.description === null ? null : (
            <p className="carte-grimoire__description">{carte.description}</p>
          )}
          {carte.effets.length === 0 ? null : (
            <div className="carte-grimoire__bloc">
              <h4 className="carte-grimoire__sousTitre">{t('grimoire.effets')}</h4>
              <ul className="carte-grimoire__effets">
                {carte.effets.map((effet, rang) => (
                  <li key={`${String(rang)}-${effet}`}>{effet}</li>
                ))}
              </ul>
            </div>
          )}
          {carte.tags.length === 0 ? null : (
            <div className="carte-grimoire__bloc">
              <h4 className="carte-grimoire__sousTitre">{t('grimoire.tags')}</h4>
              <ul className="carte-grimoire__tags">
                {carte.tags.map((tag, rang) => (
                  <li key={`${String(rang)}-${tag}`}>{tag}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="carte-grimoire__etat" data-etat="verrouillee">
          {t('grimoire.nonPossedee')}
        </p>
      )}
    </li>
  );
}
